/**
 * The search that drops from the top of the page.
 *
 * Answers in place rather than on a results page. That is not a flourish: the
 * shop's answers are products, the panel is already drawing products, and a
 * reader who has to leave the page to see five names and five prices has been
 * sent somewhere for no reason.
 *
 * The markup for those answers comes from the server as rendered cards. There
 * is one product card in this theme and one place that knows how to draw it;
 * building a second one here would be a second thing to keep right.
 */

import './index.scss';

const panel = document.querySelector('.search-panel');
const opener = document.querySelector('[data-search-open]');

if (panel && opener) {
    const form = panel.querySelector('.search-panel__form');
    const field = panel.querySelector('.search-panel__input');
    const heading = panel.querySelector('[data-search-heading]');
    const results = panel.querySelector('[data-search-results]');
    const nothing = panel.querySelector('[data-search-empty]');
    const failed = panel.querySelector('[data-search-failed]');
    const skeleton = panel.querySelector('[data-search-skeleton]');
    const status = panel.querySelector('[data-search-status]');

    /* What the panel showed before anybody typed, kept so clearing the box
     * brings it back rather than leaving the reader with a blank sheet. */
    const suggestions = results.innerHTML;
    const suggestionsHeading = heading.textContent;

    let waiting = null;
    let showing = null;
    let flight = null;
    let latest = 0;

    /**
     * Say whether an answer is on its way.
     *
     * The spinner in the box goes up at once; the ghosts under it wait a beat.
     * Most answers come back inside that beat, and a grid that blinked into
     * ghosts and back on every keystroke would be a worse thing to type into
     * than one that simply changed.
     *
     * @param {boolean} state Whether a request is in flight.
     */
    const searching = (state) => {
        window.clearTimeout(showing);

        if (!state) {
            panel.removeAttribute('data-searching');
            skeleton.hidden = true;
            results.hidden = false;
            results.removeAttribute('aria-busy');
            return;
        }

        panel.setAttribute('data-searching', 'true');
        results.setAttribute('aria-busy', 'true');

        showing = window.setTimeout(() => {
            skeleton.hidden = false;
            results.hidden = true;

            /* The grid has stopped being the popular pieces the moment it is
             * standing in for an answer, and a row of ghosts under the word
             * "Popular" says the shop is loading its best sellers. */
            heading.textContent = field.dataset.resultsLabel;
            heading.hidden = false;
        }, 180);
    };

    /**
     * Put the suggestions back.
     */
    const reset = () => {
        results.innerHTML = suggestions;
        heading.textContent = suggestionsHeading;
        heading.hidden = '' === suggestions.trim();
        nothing.hidden = true;
        failed.hidden = true;
        status.textContent = '';
    };

    /**
     * Ask the shop what matches, and draw whatever comes back.
     *
     * @param {string} term What was typed.
     */
    const ask = async (term) => {
        const mine = ++latest;

        /* Whatever was already on its way is now an answer to a question
         * nobody is asking. Cancelled rather than ignored: the connection is
         * freed and the shop stops working on it. */
        if (flight) {
            flight.abort();
        }

        flight = new AbortController();

        searching(true);
        failed.hidden = true;

        try {
            const response = await fetch(field.dataset.endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                signal: flight.signal,
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'theme_search',
                    nonce: field.dataset.nonce,
                    term,
                }).toString(),
            });

            if (!response.ok) {
                throw new Error(String(response.status));
            }

            const answer = await response.json();

            /* A slower request that started earlier must not overwrite a faster
             * one that started later. */
            if (mine !== latest) {
                return;
            }

            const found = answer?.data?.found ?? 0;
            const counted = found === 1 ? field.dataset.countOne : field.dataset.countMany;

            heading.textContent = field.dataset.resultsLabel;
            heading.hidden = false;
            results.innerHTML = answer?.data?.html || '';
            nothing.hidden = found > 0;
            status.textContent = counted.replace('%s', String(found));
        } catch (error) {
            /* A cancelled request is not a failure — it is this function being
             * asked something else. Only a real one gets reported. */
            if (error.name === 'AbortError' || mine !== latest) {
                return;
            }

            results.innerHTML = '';
            nothing.hidden = true;
            failed.hidden = false;
            status.textContent = failed.textContent.trim();
        } finally {
            if (mine === latest) {
                searching(false);
            }
        }
    };

    /**
     * Open the panel and put the cursor where the reader is about to type.
     *
     * @param {Event} event The click on the header's search icon.
     */
    const open = (event) => {
        event.preventDefault();
        panel.showModal();
        field.focus();
    };

    opener.addEventListener('click', open);

    panel.addEventListener('click', (event) => {
        if (event.target.closest('[data-search-close]')) {
            panel.close();
            return;
        }

        /* A click on the dialog itself is a click on the scrim: the panel's own
         * contents are inside `__inner`, so anything outside it is the page the
         * reader is trying to get back to. */
        if (event.target === panel) {
            panel.close();
        }
    });

    panel.addEventListener('close', () => {
        opener.focus();
    });

    /*
     * Typed, not submitted. The wait is what keeps a five-letter word from
     * being five separate questions, and the field is read again when it
     * arrives rather than being captured, so a fast typist searches for what
     * they finished writing.
     */
    field.addEventListener('input', () => {
        window.clearTimeout(waiting);

        waiting = window.setTimeout(() => {
            const term = field.value.trim();

            if (term.length < 2) {
                latest += 1;

                if (flight) {
                    flight.abort();
                }

                searching(false);
                reset();
                return;
            }

            ask(term);
        }, 350);
    });

    /*
     * Enter is already answered above, so it does not also leave the page for a
     * results screen that would say the same thing.
     */
    form.addEventListener('submit', (event) => {
        event.preventDefault();

        window.clearTimeout(waiting);

        const term = field.value.trim();

        if (term.length >= 2) {
            ask(term);
        }
    });
}
